import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// Education gate dialog (shown before the review flow for unsigned admins)
// ---------------------------------------------------------------------------

interface DRReviewEducationGateDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Warning dialog shown before an admin reviews a disaster recovery proposal for
 * the first time. Alerts them that they will need to learn about several topics
 * before they can approve or reject.
 */
export function DRReviewEducationGateDialog({
  open,
  onConfirm,
  onCancel,
}: DRReviewEducationGateDialogProps) {
  const topics = [
    'What disaster recovery is',
    'How disaster recovery works',
    'The risks of disaster recovery',
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[560px] [&>button]:hidden">
        <DialogHeader>
          <div className="flex gap-2.5 items-start">
            <AlertTriangle className="w-[18px] h-[18px] text-orange-700 shrink-0 mt-0.5" />
            <DialogTitle className="text-orange-700 text-base font-semibold leading-normal">
              Incorrectly configuring disaster recovery can compromise security
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-1 pl-[calc(18px+10px)]">
          <p className="text-sm text-zinc-600 leading-tight">
            Before you can review the proposed changes, you&apos;ll learn about
            the following topics to make sure you&apos;re properly informed:
          </p>
          {topics.map((topic) => (
            <div key={topic} className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
              <p className="text-sm text-zinc-600 leading-tight">{topic}</p>
            </div>
          ))}
        </div>
        <DialogFooter className="sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} className="flex items-center gap-2">
            Continue
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Risks confirmation dialog (gating progress from page 4 to page 5)
// ---------------------------------------------------------------------------

interface RisksConfirmDialogProps {
  open: boolean;
  checked: boolean[];
  onToggle: (index: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const RISKS_CHECKBOXES = [
  "I understand that the recovery wallet can't be changed after it has been set.",
  "I understand that it is my organization's responsibility to store the recovery wallet securely.",
  "I understand that if the recovery wallet is stolen, an attacker can use it to execute transactions on my organization's behalf",
  'I understand that if my organization loses access to the recovery wallet, we lose access to disaster recovery',
];

export function RisksConfirmDialog({
  open,
  checked,
  onToggle,
  onConfirm,
  onCancel,
}: RisksConfirmDialogProps) {
  const allChecked =
    checked.length === RISKS_CHECKBOXES.length && checked.every(Boolean);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[560px] [&>button[data-radix-dialog-close]]:hidden">
        <DialogHeader>
          <DialogTitle className="text-rose-900 text-base font-semibold">
            Confirm you understand the risks
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-5 py-2">
          {RISKS_CHECKBOXES.map((label, i) => (
            <div key={i} className="flex gap-2 items-start">
              <Checkbox
                id={`risk-check-${i}`}
                checked={checked[i] ?? false}
                onCheckedChange={() => onToggle(i)}
                className="mt-0.5"
              />
              <label
                htmlFor={`risk-check-${i}`}
                className="text-sm font-medium text-black leading-[14px] cursor-pointer"
              >
                {label}
              </label>
            </div>
          ))}
        </div>
        <DialogFooter className="sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={!allChecked}>
            I understand the risks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Wallet security confirmation dialog (gating progress from page 5 to page 6)
// ---------------------------------------------------------------------------

interface WalletSecurityConfirmDialogProps {
  open: boolean;
  checked: boolean[];
  onToggle: (index: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const WALLET_SECURITY_CHECKBOXES = [
  'I understand that the recovery wallet should only be stored using cold offline storage, multisignature wallets, or trusted third-party disaster recovery providers.',
  'I understand that the recovery wallet should only be used for MLS disaster recovery and should not be used for anything else.',
  "I understand that it is my organization's responsibility to store the recovery wallet securely.",
  'I understand the consequences if our organization fails to keep the recovery wallet secure or loses access to it.',
];

export function WalletSecurityConfirmDialog({
  open,
  checked,
  onToggle,
  onConfirm,
  onCancel,
}: WalletSecurityConfirmDialogProps) {
  const allChecked =
    checked.length === WALLET_SECURITY_CHECKBOXES.length &&
    checked.every(Boolean);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[590px] [&>button[data-radix-dialog-close]]:hidden">
        <DialogHeader>
          <DialogTitle className="text-rose-900 text-base font-semibold">
            Confirm you understand how to keep the recovery wallet secure
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-5 py-2">
          {WALLET_SECURITY_CHECKBOXES.map((label, i) => (
            <div key={i} className="flex gap-2 items-start">
              <Checkbox
                id={`wallet-sec-check-${i}`}
                checked={checked[i] ?? false}
                onCheckedChange={() => onToggle(i)}
                className="mt-0.5"
              />
              <label
                htmlFor={`wallet-sec-check-${i}`}
                className="text-sm font-medium text-black leading-[14px] cursor-pointer"
              >
                {label}
              </label>
            </div>
          ))}
        </div>
        <DialogFooter className="sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={!allChecked}>
            I understand how to keep the recovery wallet secure
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Success dialog (shown after mobile signing is complete)
// ---------------------------------------------------------------------------

interface DRSuccessDialogProps {
  open: boolean;
  requiredApprovals: number;
  onReturnToSettings: () => void;
}

function getRemainingApprovalsCopy(requiredApprovals: number): string {
  const remainingApprovals = Math.max(requiredApprovals - 1, 0);
  if (remainingApprovals === 0) {
    return 'No additional admin approvals are required.';
  }
  return `Disaster recovery setup still requires approval from ${remainingApprovals} other admin${remainingApprovals === 1 ? '' : 's'} before it takes effect.`;
}

export function DRSuccessDialog({
  open,
  requiredApprovals,
  onReturnToSettings,
}: DRSuccessDialogProps) {
  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-[430px] [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex flex-col items-center gap-6 py-2">
          <DialogHeader className="items-center w-full">
            <DialogTitle className="text-zinc-700 text-base font-semibold text-center">
              You&apos;ve successfully approved this operation
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600 text-center leading-snug">
            {getRemainingApprovalsCopy(requiredApprovals)}
          </p>
          <Button
            onClick={onReturnToSettings}
            className="flex items-center gap-2"
          >
            Return to settings
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Exit confirmation dialog
// ---------------------------------------------------------------------------

interface ExitConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ExitConfirmDialog({
  open,
  onConfirm,
  onCancel,
}: ExitConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Exit disaster recovery setup?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Your progress will be lost if you exit now.
        </p>
        <DialogFooter className="sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancel}>
            Continue setup
          </Button>
          <Button onClick={onConfirm}>Exit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Existing proposal dialog
// ---------------------------------------------------------------------------

interface ExistingDRProposalDialogProps {
  open: boolean;
  onReviewProposal: () => void;
  onCancel: () => void;
}

export function ExistingDRProposalDialog({
  open,
  onReviewProposal,
  onCancel,
}: ExistingDRProposalDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Another admin already created this proposal</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Review the existing disaster recovery proposal before approving it.
        </p>
        <DialogFooter className="sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancel}>
            Stay here
          </Button>
          <Button onClick={onReviewProposal}>Review proposal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
